import { ReactElement, useMemo } from 'react'

import { TicketFile } from 'lib/api/ticket-files/ticket-files'

import { useMediaContext } from './media-provider'
import { useAnnotationsContext } from '../providers/annotations-provider'

import { useThreeResizableContext } from 'lib/components/resizable/resizable'

import { useIsCollaboratorView } from 'providers/is-collaborator-view-provider'
import Accordion from 'lib/components/accordian/accordian'
import { TicketTracking } from 'lib/api/admin/tickets/admin-tickets'
import { displayDate } from 'lib/util/date'
import FileThumbnail from 'components/elements/files/file-thumbnail'

const classNames = {
  previewOverlayContainer: `tw-absolute
                            tw-flex
                            tw-justify-center
                            tw-items-center
                            tw-w-full
                            tw-h-full
                            tw-bg-opacity-40
                            tw-bg-black
                            tw-z-10`,

  img: 'tw-w-full tw-h-full tw-object-cover tw-aspect-square',
  imgName: 'tw-overflow-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-text-neutral-600',

  mediaItemContainer: 'tw-flex tw-flex-row tw-flex-wrap tw-gap-4 tw-px-4 tw-items-start tw-py-6',
  removeButtonAbsoluteContainer: 'tw-absolute tw--top-4 tw--right-4 tw-z-10',
  badge: 'tw-bg-cornflower-500 tw-text-white tw-rounded-md tw-px-2 tw-flex tw-items-center tw-gap-2 ',
}

export default function MediaList(): ReactElement {
  const { files, filters, showRevisions, ticket, selectFileById, selectedFile } = useMediaContext()
  const isCollaboratorView = useIsCollaboratorView()
  const { cancelAnnotation } = useAnnotationsContext()

  const { middleRef } = useThreeResizableContext()

  function openMiddlePanel() {
    middleRef.current?.expand()
  }

  const visibleFiles = useMemo(() => {
    if (filters.version) {
      return files
        .filter((file) => file.ticketVersion === filters.version)
        .sort((a, b) => parseInt(b.createdAt) - parseInt(a.createdAt))
    }
    return files
  }, [files, filters.version])

  const creativeFiles = visibleFiles.filter((file) => file.uploadedByCreative && !file.preview) || []
  const previewFiles = visibleFiles.filter((file) => file.preview) || []
  const clientFiles = visibleFiles.filter((file) => !file.uploadedByCreative) || []

  const showDivider = showRevisions && (creativeFiles.length > 0 || previewFiles.length > 0)

  return (
    <>
      {showDivider ? (
        <div className="tw-flex tw-flex-col tw-gap-2">
          {creativeFiles.length > 0 && (
            <Accordion title="Delivered" className="tw-rounded-lg tw-bg-neutral-100" openByDefault>
              <div className={classNames.mediaItemContainer}>
                {creativeFiles.map((file) => (
                  <FileThumbnail
                    size="sm"
                    key={file.id}
                    file={file}
                    focused={selectedFile?.id === file.id}
                    onClick={() => {
                      cancelAnnotation()
                      selectFileById(file.id, null)
                      openMiddlePanel?.()
                    }}
                    actions={!isCollaboratorView}
                  />
                ))}
              </div>
            </Accordion>
          )}

          {previewFiles.length > 0 && (
            <Accordion
              title="Preview"
              className="tw-rounded-lg tw-bg-neutral-100"
              openByDefault={creativeFiles.length === 0}
            >
              <PreviewFiles
                previewFiles={previewFiles}
                openMiddlePanel={openMiddlePanel}
                ticketTrackings={ticket.ticketTrackings}
              />
            </Accordion>
          )}

          {clientFiles.length > 0 && (
            <Accordion
              title="Added"
              className="tw-rounded-lg tw-bg-neutral-100"
              openByDefault={creativeFiles.length === 0 && previewFiles.length === 0}
            >
              <div className={classNames.mediaItemContainer}>
                {clientFiles.map((file) => (
                  <FileThumbnail
                    size="sm"
                    key={file.id}
                    file={file}
                    canDelete={file.meta?.permissions?.destroy}
                    focused={selectedFile?.id === file.id}
                    onClick={() => {
                      cancelAnnotation()
                      selectFileById(file.id, null)
                      openMiddlePanel?.()
                    }}
                    actions={!isCollaboratorView}
                  />
                ))}
              </div>
            </Accordion>
          )}
        </div>
      ) : (
        <div className={classNames.mediaItemContainer}>
          {visibleFiles.map((file) => (
            <FileThumbnail
              size="sm"
              file={file}
              key={file.id}
              canDelete={file.meta?.permissions?.destroy}
              onClick={() => {
                cancelAnnotation()
                selectFileById(file.id, null)
                openMiddlePanel?.()
              }}
              focused={selectedFile?.id === file.id}
              actions={!isCollaboratorView}
            />
          ))}
        </div>
      )}
    </>
  )
}

interface PreviewFilesProps {
  previewFiles: TicketFile[]
  openMiddlePanel: () => void
  ticketTrackings: TicketTracking[]
}

function PreviewFiles({ previewFiles, openMiddlePanel, ticketTrackings }: PreviewFilesProps) {
  const { selectFileById, selectedFile } = useMediaContext()
  const { cancelAnnotation } = useAnnotationsContext()
  const isCollaboratorView = useIsCollaboratorView()

  const previewFilesByTicketTracking = useMemo(() => {
    const filesByTicketTracking = ticketTrackings
      ?.sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime())
      ?.map((ticketTracking) => {
        return {
          files: previewFiles.filter(
            (file) =>
              new Date(file.createdAt) >= new Date(ticketTracking.startTime) &&
              new Date(file.createdAt) <= new Date(ticketTracking.endTime),
          ),
          ticketTracking,
        }
      })

    return filesByTicketTracking
  }, [previewFiles, ticketTrackings])

  return (
    <div>
      {previewFilesByTicketTracking
        .filter(({ files }) => files.length > 0)
        .map(({ files, ticketTracking }, index) => (
          <div key={ticketTracking.id} className="tw-m-4 tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-bg-white">
            <Accordion title={displayDate(ticketTracking.endTime)} openByDefault={index === 0}>
              <div className={classNames.mediaItemContainer}>
                {files.map((file) => (
                  <FileThumbnail
                    size="sm"
                    key={file.id}
                    file={file}
                    onClick={() => {
                      cancelAnnotation()
                      selectFileById(file.id, null)
                      openMiddlePanel?.()
                    }}
                    focused={selectedFile?.id === file.id}
                    actions={!isCollaboratorView}
                  />
                ))}
              </div>
            </Accordion>
          </div>
        ))}
    </div>
  )
}
